/**
 * Meeting Scheduler API - Booking links and scheduled meetings
 */
import apiClient from './index'

export interface DayAvailability {
  enabled: boolean
  start: string
  end: string
}

export interface WeeklyAvailability {
  monday: DayAvailability
  tuesday: DayAvailability
  wednesday: DayAvailability
  thursday: DayAvailability
  friday: DayAvailability
  saturday: DayAvailability
  sunday: DayAvailability
}

export interface BookingLink {
  id: string
  slug: string
  user_id: string
  user_name: string
  user_email: string
  title: string
  description?: string
  duration_minutes: number
  location_type: 'video' | 'phone' | 'in_person'
  location_details?: string
  availability: WeeklyAvailability
  buffer_before: number
  buffer_after: number
  min_notice_hours: number
  max_days_ahead: number
  is_active: boolean
  booking_url: string
  created_at: string
  updated_at: string
}

export interface ScheduledMeeting {
  id: string
  booking_link_id: string
  host_user_id: string
  host_name: string
  host_email: string
  guest_name: string
  guest_email: string
  guest_phone?: string
  guest_notes?: string
  contact_id?: string
  title: string
  start_time: string
  end_time: string
  duration_minutes: number
  location_type: string
  location_details?: string
  status: 'confirmed' | 'cancelled' | 'completed' | 'no_show'
  cancelled_at?: string
  cancelled_by?: string
  cancellation_reason?: string
  created_at: string
}

export interface TimeSlot {
  start: string
  end: string
}

export interface BookingLinkResponse {
  data: BookingLink
}

export interface BookingLinksResponse {
  data: BookingLink[]
}

export interface ScheduledMeetingResponse {
  data: ScheduledMeeting
}

export interface ScheduledMeetingsResponse {
  data: ScheduledMeeting[]
}

export interface TimeSlotsResponse {
  data: TimeSlot[]
}

// ============ Booking Links ============

/**
 * Get all booking links for current user
 */
export async function getBookingLinks(): Promise<BookingLink[]> {
  const response = await apiClient.get<BookingLinksResponse>('/booking-links')
  return response.data.data
}

/**
 * Create a new booking link
 */
export async function createBookingLink(data: {
  title: string
  description?: string
  duration_minutes?: number
  location_type?: 'video' | 'phone' | 'in_person'
  location_details?: string
  availability?: Partial<WeeklyAvailability>
  buffer_before?: number
  buffer_after?: number
  min_notice_hours?: number
  max_days_ahead?: number
  is_active?: boolean
}): Promise<BookingLink> {
  const response = await apiClient.post<BookingLinkResponse>('/booking-links', data)
  return response.data.data
}

/**
 * Get a booking link by ID
 */
export async function getBookingLink(linkId: string): Promise<BookingLink> {
  const response = await apiClient.get<BookingLinkResponse>(`/booking-links/${linkId}`)
  return response.data.data
}

/**
 * Update a booking link
 */
export async function updateBookingLink(
  linkId: string,
  data: Partial<BookingLink>
): Promise<BookingLink> {
  const response = await apiClient.put<BookingLinkResponse>(`/booking-links/${linkId}`, data)
  return response.data.data
}

/**
 * Delete a booking link
 */
export async function deleteBookingLink(linkId: string): Promise<void> {
  await apiClient.delete(`/booking-links/${linkId}`)
}

// ============ Public Booking ============

/**
 * Get public booking link info (no auth)
 */
export async function getPublicBookingLink(slug: string): Promise<{
  title: string
  description?: string
  host_name: string
  duration_minutes: number
  location_type: string
  availability: WeeklyAvailability
  min_notice_hours: number
  max_days_ahead: number
}> {
  const response = await apiClient.get(`/book/${slug}`)
  return response.data.data
}

/**
 * Get available time slots for a date
 */
export async function getAvailableSlots(slug: string, date: string): Promise<TimeSlot[]> {
  const response = await apiClient.get<TimeSlotsResponse>(
    `/book/${slug}/available-slots?date=${date}`
  )
  return response.data.data
}

/**
 * Create a booking (public, no auth)
 */
export async function createBooking(
  slug: string,
  data: {
    guest_name: string
    guest_email: string
    guest_phone?: string
    guest_notes?: string
    start_time: string
  }
): Promise<ScheduledMeeting> {
  const response = await apiClient.post<ScheduledMeetingResponse>(`/book/${slug}`, data)
  return response.data.data
}

// ============ Scheduled Meetings ============

/**
 * Get scheduled meetings for current user
 */
export async function getScheduledMeetings(options?: {
  status?: string
  upcoming?: boolean
  limit?: number
}): Promise<ScheduledMeeting[]> {
  const params = new URLSearchParams()

  if (options?.status) {
    params.set('status', options.status)
  }
  if (options?.upcoming) {
    params.set('upcoming', 'true')
  }
  if (options?.limit) {
    params.set('limit', options.limit.toString())
  }

  const url = params.toString()
    ? `/scheduled-meetings?${params.toString()}`
    : '/scheduled-meetings'
  const response = await apiClient.get<ScheduledMeetingsResponse>(url)
  return response.data.data
}

/**
 * Get a scheduled meeting by ID
 */
export async function getScheduledMeeting(meetingId: string): Promise<ScheduledMeeting> {
  const response = await apiClient.get<ScheduledMeetingResponse>(
    `/scheduled-meetings/${meetingId}`
  )
  return response.data.data
}

/**
 * Cancel a scheduled meeting
 */
export async function cancelScheduledMeeting(
  meetingId: string,
  reason?: string
): Promise<ScheduledMeeting> {
  const response = await apiClient.post<ScheduledMeetingResponse>(
    `/scheduled-meetings/${meetingId}/cancel`,
    { reason }
  )
  return response.data.data
}

/**
 * Delete a scheduled meeting
 */
export async function deleteScheduledMeeting(meetingId: string): Promise<void> {
  await apiClient.delete(`/scheduled-meetings/${meetingId}`)
}
